import {
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useTransition,
} from 'react'
import styled, { css } from 'styled-components'
import Link from 'next/link'
import { noImagUrl } from '../../../../utils/constants'
import { SCREEN_SIZE } from '../../../../lib/Media'
import AllCategoryCard from './AllCategoryCard'
import { CategoryCollectionWrapper } from './style'
import {
  usePathname,
  useSearchParams,
  useRouter,
  useParams,
} from 'next/navigation'
import { useSelector } from 'react-redux'
import OptimizedImage from '../../../common/OptimizedImage/Image'
import Image from 'next/image'

const CollectionContainer = styled.div`
  overflow-x: ${({ layoutType, isCategoryPage }) =>
    layoutType === 'GRID' || isCategoryPage ? 'unset' : 'scroll'};
  position: ${({ isCategoryPage }) => isCategoryPage && 'sticky'};
  top: ${({ isCategoryPage }) => isCategoryPage && '12rem'};
  scrollbar-width: none;
  box-shadow: ${({ categoryPage }) =>
    categoryPage ? '0 0 10px 0 rgba(0,0,0,0.1)' : 'none'};
  border-radius: ${({ categoryPage }) => categoryPage && '10px'};
  padding: ${({ categoryPage }) => categoryPage && '10px 0'};
  margin-bottom: ${({ categoryPage }) => categoryPage && '1rem'};
`

export const Collection = styled.div`
  display: flex;
  flex-wrap: ${({ layoutType }) => layoutType === 'GRID' && 'wrap'};
  flex-direction: ${({ categoryPage }) => categoryPage && 'column'};
  gap: ${({ categoryPage, shape }) =>
    !categoryPage && shape !== 'Rectangle'
      ? '2.3rem'
      : shape === 'Rectangle'
      ? '1.6rem'
      : ''};
  ${SCREEN_SIZE.Below.MobileL} {
    padding: 5px;
    height: ${({ categoryPage }) => categoryPage && '70vh'};
    overflow-y: ${({ categoryPage }) => categoryPage && 'auto'};
    ${({ categoryPage, shape }) =>
      !categoryPage &&
      shape !== 'Rectangle' &&
      css`
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 1.5rem;
      `}
  }
`

export const CategoryCard = styled.a`
  display: flex;
  flex-direction: ${({ categoryPage }) => (categoryPage ? 'row' : 'column')};
  align-items: center;
  gap: 2rem;
  padding: ${({ categoryPage }) => (categoryPage ? '1.2rem' : '0')};
  transition: box-shadow 200ms ease-in;
  cursor: pointer;
  border-bottom: ${({ categoryPage, lastElement }) =>
    categoryPage && !lastElement && '1px solid #d8d8d8'};
  background-color: ${({ active, categoryPage }) =>
    active && categoryPage ? '#fff3f3' : 'transparent'};
  border-left: ${({ categoryPage, active, theme }) =>
    categoryPage && active && `6px solid ${theme.primaryColor}`};
  border-top-left-radius: ${({ categoryPage, active }) =>
    categoryPage && active && `6px `};
  border-bottom-left-radius: ${({ categoryPage, active }) =>
    categoryPage && active && `6px `};

  ${SCREEN_SIZE.Below.MobileL} {
    flex-direction: column;
    gap: 0.5rem;
    padding: ${({ categoryPage }) => (categoryPage ? '0.5rem' : '0')};
  }
`

const getCategoryImageHeight = (shape, categoryPage) => {
  if (shape === 'Rectangle') {
    return '17rem'
  }
  return categoryPage ? '6rem' : '13rem'
}

const getCategoryImageWidth = (shape, categoryPage) => {
  if (shape === 'Rectangle') {
    return '39.8rem'
  }
  return categoryPage ? '6rem' : '13rem'
}
const getCategoryImageHeightMobile = (shape, categoryPage) => {
  if (shape === 'Rectangle') {
    return '11rem'
  }
  return categoryPage ? '6rem' : '9rem'
}
const getCategoryImageWidthMobile = (shape, categoryPage) => {
  if (shape === 'Rectangle') {
    return '24rem'
  }
  return categoryPage ? '6rem' : '9rem'
}
export const CategoryImage = styled.div`
  background-color: rgba(255, 255, 255, 1);
  height: ${({ shape, categoryPage }) =>
    getCategoryImageHeight(shape, categoryPage)};
  width: ${({ shape, categoryPage }) =>
    getCategoryImageWidth(shape, categoryPage)};
  aspect-ratio: 1;
  ${SCREEN_SIZE.Below.MobileL} {
    height: ${({ shape, categoryPage }) =>
      getCategoryImageHeightMobile(shape, categoryPage)};
    width: ${({ shape, categoryPage }) =>
      getCategoryImageWidthMobile(shape, categoryPage)};
  }
`

export const CategoryImageStyled = styled.img`
  height: 100%;
  width: 100%;
  border-radius: ${({ categoryPage }) => !categoryPage && '18.4px'};
  object-fit: fill;
  aspect-ratio: 1;
`

export const CategoryName = styled.span`
  color: rgb(35, 41, 53);
  font-size: 14px;
  line-height: 1.2;
  text-align: center;
  margin-top: 4px;
  display: ${({ shape }) => shape !== 'Rectangle' && 'flex'};
  flex-wrap: ${({ shape }) => shape !== 'Rectangle' && 'wrap'};
  justify-content: ${({ shape }) => shape !== 'Rectangle' && 'center'};
  line-height: ${({ shape }) => shape !== 'Rectangle' && 1.3};
  font-weight: ${({ active, categoryPage }) =>
    active && categoryPage ? '600' : '400'};
  width: ${({ shape, categoryPage }) =>
    shape !== 'Rectangle' && !categoryPage && '13.5rem'};
  color: ${({ active, theme, categoryPage }) =>
    active && categoryPage && `${theme.primaryColor}`};

  ${({ page }) =>
    page === 'CATEGORY' &&
    `
    text-align: left;
  `}
  ${SCREEN_SIZE.Below.MobileL} {
    font-size: 1rem;
    width: unset;
  }
`

export const CategoryWrp = styled.div`
  background-color: ${({ active, categoryPage }) =>
    active && categoryPage ? '#fff3f3' : 'transparent'};
  border-left: ${({ categoryPage, active, theme }) =>
    categoryPage && active && `6px solid ${theme.primaryColor}`};
  border-top-left-radius: ${({ categoryPage, active }) =>
    categoryPage && active && `6px `};
  border-bottom-left-radius: ${({ categoryPage, active }) =>
    categoryPage && active && `6px `};
`

const generateBlurDataURL = () => {
  // Create a tiny 10x10 canvas
  const canvas = document.createElement('canvas')
  canvas.width = 300 // Specify a fixed width
  canvas.height = 300 // Specify a fixed height
  const ctx = canvas.getContext('2d')

  // Fill with a light gray color
  ctx.fillStyle = '#f0f0f0'
  ctx.fillRect(0, 0, 300, 300) // Fill the entire canvas

  // Return the data URL
  return canvas.toDataURL('image/jpeg', 0.1)
}

function CategoryCollection(props) {
  const { layoutType, shape } = props.config || ''
  const {
    page,
    name = '',
    primaryCategory,
    isCategoryPage,
    parentSlug,
    onCategoryClick,
  } = props
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const params = useParams()
  const { categoryName } = params
  const [activeCategory, setActiveCategory] = useState(null)

  // Set initial active category from URL
  useEffect(() => {
    const filterParam = searchParams.get('filter')
    if (filterParam) {
      const decodedFilter = decodeURIComponent(filterParam)
      const filters = decodedFilter.split('&')
      const categoryFilter = filters.find((filter) =>
        filter.startsWith('category:')
      )
      if (categoryFilter) {
        const categorySlug = categoryFilter.split(':')[1]
        setActiveCategory(categorySlug)
      }
    }
  }, [])

  const handleCategoryClick = useCallback(
    async (categorySlug) => {
      if (activeCategory === categorySlug) return

      setActiveCategory(categorySlug)

      if (onCategoryClick) {
        await onCategoryClick(categorySlug)
      }
    },
    [activeCategory, onCategoryClick]
  )

  const getCategoryFromFilter = () => {
    const filterParam = searchParams.get('filter')
    if (!filterParam) return null

    const decodedFilter = decodeURIComponent(filterParam)

    const filters = decodedFilter.split('&')

    const categoryFilter = filters.find((filter) =>
      filter.startsWith('category:')
    )
    if (categoryFilter) {
      return categoryFilter.split(':')[1]
    }
    return null
  }

  const subCat = getCategoryFromFilter()
  const categories = props?.data?.collection?.map((x) => x.category)

  useEffect(() => {
    if (categories?.length) {
      categories.forEach((category) => {
        const type = category?.entityType === 'TAG' ? 'tag' : 'category'
        router.prefetch(`/${type}/${category.slug}`)
      })
    }
  }, [categories, router])

  const categoryPage = pathname.includes('/category/')
  const { navigationData } = useSelector((state) => state.commonData)
  const data = useMemo(
    () => (navigationData && navigationData[0]) || [],
    [navigationData]
  )

  const slug =
    categories.find((item) => item.name === primaryCategory)?.slug ?? null

  return (
    <>
      {name === 'AllCategories' ? (
        <CategoryCollectionWrapper grid>
          {props?.data?.map((item) => (
            <AllCategoryCard shape={shape} item={item} key={item.id} />
          ))}
        </CategoryCollectionWrapper>
      ) : (
        <CollectionContainer
          className={page === 'CATEGORY' ? 'category' : 'root'}
          layoutType={layoutType}
          shape={shape}
          categoryPage={categoryPage}
          isCategoryPage={page === 'CATEGORY'}
        >
          <Collection
            layoutType={layoutType}
            categoryPage={categoryPage}
            shape={shape}
          >
            {page !== 'CATEGORY' &&
              categories.map((category, index) => {
                const type = category?.entityType === 'TAG' ? 'tag' : 'category'
                const isActive =
                  activeCategory === category?.slug || slug === category?.slug

                return (
                  <CategoryWrp
                    key={category.id}
                    active={isActive}
                    categoryPage={categoryPage}
                  >
                    {page !== 'CATEGORY' && (
                      <Link
                        as={`/${type}/${category.slug}`}
                        href={`/${type}/[${type}Name]`}
                        passHref
                        legacyBehavior
                      >
                        <CategoryCard className="category-card">
                          <CategoryImage
                            shape={shape}
                            categoryPage={categoryPage}
                          >
                            <OptimizedImage
                              height={'100%'}
                              width={'100%'}
                              src={category.image || noImagUrl}
                              alt={`category-image-${index}`}
                              priority
                              loading={'eager'}
                              style={{
                                borderRadius: !categoryPage && '18.4px',
                              }}
                            />
                          </CategoryImage>
                          <CategoryName
                            page={page}
                            shape={shape}
                            categoryPage={categoryPage}
                          >
                            {category.name}
                          </CategoryName>
                        </CategoryCard>
                      </Link>
                    )}
                  </CategoryWrp>
                )
              })}
            {isCategoryPage &&
              categories.map((category, index) => {
                const lastElement = index === categories.length - 1
                const categorySlug = category.url.substring(
                  category.url.lastIndexOf('/') + 1
                )

                const isActive =
                  activeCategory === categorySlug ||
                  (parentSlug === categorySlug && !activeCategory)

                return (
                  <CategoryCard
                    key={`Category-Card-${index}`}
                    className={`category-card ${isActive ? 'active' : ''}`}
                    active={isActive}
                    onClick={() => handleCategoryClick(categorySlug)}
                    shape={shape}
                    categoryPage={categoryPage}
                    lastElement={lastElement}
                  >
                    <CategoryImage shape={shape} categoryPage={categoryPage}>
                      <OptimizedImage
                        height="100%"
                        width="100%"
                        src={category.image || noImagUrl}
                        alt={`category-image-${index}`}
                        priority
                        loading={'eager'}
                        style={{
                          borderRadius: !categoryPage && '18.4px',
                        }}
                      />
                    </CategoryImage>
                    <CategoryName
                      page={page}
                      categoryPage={categoryPage}
                      active={isActive}
                    >
                      {category?.name}
                    </CategoryName>
                  </CategoryCard>
                )
              })}
          </Collection>
        </CollectionContainer>
      )}
    </>
  )
}

export default CategoryCollection
